import { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetDevices } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import { IDevice } from '../../../@types/model/masterData/device/device';
import DeviceHttpService from '../../../services/http/masterData/deviceHttpService';
import { IDeviceFormValues } from '../../../@types/model/masterData/device/deviceFormValues';
import DeviceForm from './form/DeviceForm';
import { IApiError } from '../../../@types/other';
import { setDeviceMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { useLocation } from 'react-router-dom';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';
import UserHelper from '../../../services/helper/userHelper';
import DeviceModelHelper from '../../../@types/model/masterData/device/deviceModelHelper';

const DeviceScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const devices = useAppSelector(x => x.masterData.devices);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedDevice, setSelectedDevice] = useState<IDevice | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [deletingDevice, setDeletingDevice] = useState<IDevice | null>(null);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await DeviceHttpService.getDeviceData();
                dispatch(dataSetDevices(res.data));
            } catch (error : unknown) {
                const apiError = error as IApiError;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading devices.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onSubmit = async (values : IDeviceFormValues) => {
        setIsLoading(true);

        try {
            const upsert = DeviceModelHelper.createUpsert(values);
            const res = await DeviceHttpService.addOrUpdateDevice(upsert);
            const updatedList = upsertArrayElement(devices, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetDevices(updatedList));
            await setDeviceMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedDevice ? 'Device updated successfully.' : 'Device added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating device data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeDevice = async () => {
        if (!deletingDevice) return;
        const updatedDevice = { ...deletingDevice, isActive: false };
        setIsLoading(true);
        try {
            const res = await DeviceHttpService.deleteDevice(updatedDevice.id);
            const updatedList = upsertArrayElement(devices, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetDevices(updatedList));
            await setDeviceMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Device deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting device.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedDevice(null);
    };

    const edit = (selectedRow : IDevice) => {
        setSelectedDevice(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IDevice) : void => {
        setDeletingDevice(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingDevice(null);
    };

    const deleteButtonTooltip = (row : IDevice) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Device', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo<IDeviceFormValues>(() => {
        return DeviceModelHelper.createFormValues(selectedDevice);
    }, [selectedDevice, isDialogOpen]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IDevice>
                        
                        enableAdding
                        // addTooltipRightValidation={!userHasRights.addRight}
                        disableAddButton={!userHasRights.addRight || isLoading}
                        addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Device', RightGrantsEnum.Add) : 'Add'}
                        addFunction={openModal}
                        // addTooltipOnClick={() : void => requestAccess('Add')}
                    
                        enableEditing
                        // editTooltipRightValidation={!userHasRights.editRight}
                        disableEditButton={(row : IDevice) => !userHasRights.editRight || isLoading}
                        editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Device', RightGrantsEnum.Edit) : 'Edit'}
                        editFunction={edit}
                        // editTooltipOnClick={() : void => requestAccess('Edit')}

                        enableDeleting
                        // deleteTooltipRightValidation={!userHasRights.deleteRight}
                        disableDeleteButton={(row : IDevice) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                        deleteTooltip={deleteButtonTooltip}
                        deleteFunction={onDelete}
                        // deleteTooltipOnClick={() : void => requestAccess('Delete')}
                    
                        enableSorting
                        enableFiltering
                        enablePagination
                        fitWidthToPage
                        columns={[
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Serial Number', field: 'serialNumber', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={devices}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            {isDialogOpen &&
                <ProjectDialog
                    title={selectedDevice ? 'Edit Device' : 'Add Device'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'md'}
                >
                    <DeviceForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Device'}
                open={!!deletingDevice}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeDevice}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default DeviceScreen;
